/**
 * Cohort Analysis Page
 * Retention matrix showing customer retention by acquisition month
 */

import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { advancedAnalyticsApi } from '../lib/api/analytics';
import { CohortAnalysis } from '../types/analytics';
import { CohortHeatmap } from '../components/analytics/CohortHeatmap';
import { MetricCard } from '../components/analytics/MetricCard';

export default function CohortAnalysisPage() {
  const [loading, setLoading] = useState(true);
  const [cohortData, setCohortData] = useState<CohortAnalysis | null>(null);
  const [dateRange, setDateRange] = useState<'3m' | '6m' | '1y' | 'all'>('6m');

  useEffect(() => {
    loadData();
  }, [dateRange]);

  const loadData = async () => {
    setLoading(true);
    try {
      const now = new Date();
      let startDate: Date | undefined;

      switch (dateRange) {
        case '3m':
          startDate = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000);
          break;
        case '6m':
          startDate = new Date(now.getTime() - 180 * 24 * 60 * 60 * 1000);
          break;
        case '1y':
          startDate = new Date(now.getTime() - 365 * 24 * 60 * 60 * 1000);
          break;
        default:
          startDate = undefined;
      }

      const params = startDate
        ? {
            startDate: startDate.toISOString().split('T')[0],
            endDate: now.toISOString().split('T')[0],
          }
        : undefined;

      const data = await advancedAnalyticsApi.getCohortAnalysis(params);
      setCohortData(data);
    } catch (error) {
      console.error('Failed to load cohort analysis:', error);
      alert('Errore nel caricamento della Cohort Analysis');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Caricamento Cohort Analysis...</p>
        </div>
      </div>
    );
  }

  if (!cohortData) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center text-gray-500">
          <p>Nessun dato disponibile</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Cohort Analysis</h1>
              <p className="text-gray-600 mt-1">Retention matrix per mese di acquisizione</p>
            </div>
            <div className="flex items-center gap-2">
              <select
                value={dateRange}
                onChange={(e) => setDateRange(e.target.value as any)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              >
                <option value="3m">Ultimi 3 mesi</option>
                <option value="6m">Ultimi 6 mesi</option>
                <option value="1y">Ultimo anno</option>
                <option value="all">Tutto il periodo</option>
              </select>
              <button
                onClick={loadData}
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors flex items-center gap-2"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                  />
                </svg>
                Aggiorna
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8 space-y-8">
        {/* Key Metrics */}
        <div>
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Retention Rate Medio</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <MetricCard
              title="Retention @ Mese 1"
              value={`${cohortData.averageRetentionMonth1.toFixed(1)}%`}
              icon={
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              }
              color={cohortData.averageRetentionMonth1 >= 50 ? 'green' : cohortData.averageRetentionMonth1 >= 30 ? 'amber' : 'red'}
              subtitle="Clienti che ritornano dopo 1 mese"
            />
            <MetricCard
              title="Retention @ Mese 3"
              value={`${cohortData.averageRetentionMonth3.toFixed(1)}%`}
              icon={
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                  />
                </svg>
              }
              color={cohortData.averageRetentionMonth3 >= 40 ? 'green' : cohortData.averageRetentionMonth3 >= 25 ? 'amber' : 'red'}
              subtitle="Clienti che ritornano dopo 3 mesi"
            />
            <MetricCard
              title="Retention @ Mese 6"
              value={`${cohortData.averageRetentionMonth6.toFixed(1)}%`}
              icon={
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                  />
                </svg>
              }
              color={cohortData.averageRetentionMonth6 >= 30 ? 'green' : cohortData.averageRetentionMonth6 >= 20 ? 'amber' : 'red'}
              subtitle="Clienti che ritornano dopo 6 mesi"
            />
          </div>
        </div>

        {/* Retention Heatmap */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
        >
          <div className="mb-6">
            <h2 className="text-xl font-semibold text-gray-900">Retention Matrix</h2>
            <p className="text-sm text-gray-600 mt-1">
              Ogni riga rappresenta una cohort (mese di acquisizione). Ogni cella mostra la % di clienti ancora attivi.
            </p>
          </div>
          <CohortHeatmap cohorts={cohortData.cohorts} />
        </motion.div>

        {/* Insights */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl shadow-sm border border-blue-200 p-6"
        >
          <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <svg className="w-6 h-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
              />
            </svg>
            Insights & Raccomandazioni
          </h2>
          <div className="space-y-3">
            {cohortData.averageRetentionMonth1 < 40 && (
              <div className="bg-white rounded-lg p-4 border-l-4 border-amber-500">
                <p className="font-medium text-amber-900">⚠️ Retention Month 1 bassa</p>
                <p className="text-sm text-gray-700 mt-1">
                  Solo {cohortData.averageRetentionMonth1.toFixed(0)}% dei clienti torna dopo il primo mese.
                  Considera di implementare una campagna di benvenuto automatica o un programma fedeltà.
                </p>
              </div>
            )}
            {cohortData.averageRetentionMonth3 < 30 && (
              <div className="bg-white rounded-lg p-4 border-l-4 border-red-500">
                <p className="font-medium text-red-900">🚨 Churn rate alto a 3 mesi</p>
                <p className="text-sm text-gray-700 mt-1">
                  Retention a 3 mesi è solo {cohortData.averageRetentionMonth3.toFixed(0)}%.
                  Implementa email di retention al giorno 60 con offerta speciale.
                </p>
              </div>
            )}
            {cohortData.averageRetentionMonth1 >= 50 && cohortData.averageRetentionMonth6 >= 30 && (
              <div className="bg-white rounded-lg p-4 border-l-4 border-green-500">
                <p className="font-medium text-green-900">✅ Ottima retention!</p>
                <p className="text-sm text-gray-700 mt-1">
                  I tuoi clienti sono fedeli. Mantieni questa strategia e considera un programma VIP per i top spenders.
                </p>
              </div>
            )}
            <div className="bg-white rounded-lg p-4 border-l-4 border-blue-500">
              <p className="font-medium text-blue-900">💡 Pro Tip</p>
              <p className="text-sm text-gray-700 mt-1">
                La retention ideale per ristoranti è: Month 1 = 45-55%, Month 3 = 30-40%, Month 6 = 20-30%.
                Se sei sopra questi valori, stai andando benissimo!
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
